import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type FormEvent } from 'react';
import {
  adminApi,
  type CommandInput,
  type CommandMaster,
  type CommandScope,
  type Project,
  type Team,
} from '../api/admin';

// /コマンドマスタ管理（AI-WF #91 / #98）。スコープ(global/project/team)ごとに画面を分割し、
// 件数が増えても見やすくする。各画面は自スコープのみ表示・登録（scope はページ＝自明なので
// フォームから撤去）。検索はコマンド名・表示名・説明を対象（クライアント側・スコープ内）。
// 打鍵時のリモート挙動（連携仕様）は別タスク。ここは諸元の CRUD のみ。

const KINDS: { code: string; label: string }[] = [
  { code: 'builtin', label: 'claude純正（/clear 等）' },
  { code: 'skill', label: 'スキル発動' },
  { code: 'custom', label: 'カスタム（/本番デプロイ 等）' },
  { code: 'intent', label: '意図シグナル（/仕様 等）' },
  { code: 'exec', label: '直接OSオペ（/ls 等）' },
];
const RUNS_ON: { code: string; label: string }[] = [
  { code: 'claude-session', label: 'claude-session' },
  { code: 'ai-workflow', label: 'ai-workflow' },
  { code: 'remote-exec', label: 'remote-exec' },
  { code: 'intent', label: 'intent' },
];
const INVOKE_BY: { code: string; label: string }[] = [
  { code: 'owner', label: 'owner（社長のみ・既定）' },
  { code: 'role', label: 'role' },
  { code: 'team', label: 'team' },
];
const SCOPE_META: Record<CommandScope, { title: string; icon: string; desc: string }> = {
  global: {
    title: 'global コマンド',
    icon: '🌐',
    desc: 'システム全体に効く claude 純正等のコマンド。全プロジェクト・全チームで使えます。',
  },
  project: {
    title: 'project コマンド',
    icon: '📁',
    desc: 'プロジェクト単位のコマンド。そのプロジェクトのチャットで使えます。',
  },
  team: {
    title: 'team コマンド',
    icon: '👥',
    desc: 'プロジェクトのチーム単位のコマンド。同名は team が global / project を上書きします。',
  },
};

const labelOf = (list: { code: string; label: string }[], code: string) =>
  list.find((x) => x.code === code)?.label ?? code;

export function CommandMasterPage({ scope }: { scope: CommandScope }) {
  const qc = useQueryClient();
  const meta = SCOPE_META[scope];
  // 自スコープのみ取得（サーバ側 scope 絞り込み）。queryKey に scope を含めて画面間で混ざらない。
  const { data: commands, isLoading } = useQuery({
    queryKey: ['commands', scope],
    queryFn: () => adminApi.listCommands({ scope }),
  });
  // project/team 画面のみ対象セレクタ用に一覧を引く（global では不要）。
  const needProject = scope === 'project' || scope === 'team';
  const needTeam = scope === 'team';
  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: () => adminApi.listProjects(),
    enabled: needProject,
  });
  const { data: teams } = useQuery({
    queryKey: ['teams'],
    queryFn: () => adminApi.listTeams(),
    enabled: needTeam,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['commands', scope] });
  const [newOpen, setNewOpen] = useState(false);
  const [q, setQ] = useState('');

  // 検索: コマンド名 / 表示名 / 説明 の部分一致（スコープ内・クライアント側）。
  const filtered = useMemo(() => {
    const rows = commands ?? [];
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(
      (c) =>
        c.name.toLowerCase().includes(s) ||
        c.label.toLowerCase().includes(s) ||
        (c.description ?? '').toLowerCase().includes(s),
    );
  }, [commands, q]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0 }}>
            {meta.icon} {meta.title}
          </h2>
          <p className="k-muted" style={{ margin: '4px 0 0', fontSize: 13.5 }}>{meta.desc}</p>
        </div>
        <button type="button" onClick={() => setNewOpen(true)}>
          ＋ コマンドを追加
        </button>
      </div>

      <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
        <input
          placeholder="コマンド名・表示名・説明で検索…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ flex: 1, maxWidth: 420 }}
        />
        <span className="k-muted" style={{ fontSize: 12 }}>
          {filtered.length}
          {q.trim() ? ` / ${(commands ?? []).length}` : ''} 件
        </span>
      </div>

      {newOpen && (
        <CommandForm
          scope={scope}
          projects={projects ?? []}
          teams={teams ?? []}
          onClose={() => setNewOpen(false)}
          onSubmit={async (v) => {
            await adminApi.createCommand(v);
            await invalidate();
            setNewOpen(false);
          }}
        />
      )}

      {isLoading && <p className="k-muted" style={{ marginTop: 16 }}>読み込み中…</p>}

      <table className="k-table" style={{ marginTop: 14, width: '100%' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', width: 50 }}>順</th>
            <th style={{ textAlign: 'left' }}>コマンド</th>
            <th style={{ textAlign: 'left', width: 90 }}>種別</th>
            <th style={{ textAlign: 'left' }}>実行先</th>
            {needProject && <th style={{ textAlign: 'left' }}>適用先</th>}
            <th style={{ textAlign: 'left', width: 70 }}>状態</th>
            <th style={{ width: 140 }}></th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((c) => (
            <CommandRow
              key={c.id}
              cmd={c}
              scope={scope}
              projects={projects ?? []}
              teams={teams ?? []}
              onChanged={invalidate}
            />
          ))}
          {!isLoading && filtered.length === 0 && (
            <tr>
              <td colSpan={needProject ? 7 : 6} className="k-muted" style={{ padding: '12px 4px' }}>
                {q.trim()
                  ? '検索に一致するコマンドがありません。'
                  : 'このスコープのコマンドはまだありません。右上の「＋ コマンドを追加」から登録してください。'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function CommandRow({
  cmd,
  scope,
  projects,
  teams,
  onChanged,
}: {
  cmd: CommandMaster;
  scope: CommandScope;
  projects: Project[];
  teams: Team[];
  onChanged: () => void;
}) {
  const [editOpen, setEditOpen] = useState(false);
  const del = useMutation({
    mutationFn: () => adminApi.deleteCommand(cmd.id),
    onSuccess: onChanged,
  });
  const needProject = scope === 'project' || scope === 'team';
  const colSpan = needProject ? 7 : 6;

  const target =
    scope === 'team'
      ? teams.find((t) => t.id === cmd.teamId)?.name ?? `team#${cmd.teamId ?? '-'}`
      : scope === 'project'
        ? projects.find((p) => p.id === cmd.projectId)?.nickname ??
          projects.find((p) => p.id === cmd.projectId)?.name ??
          `project#${cmd.projectId ?? '-'}`
        : null;

  return (
    <>
      <tr>
        <td>{cmd.sort}</td>
        <td>
          <code style={{ fontSize: 13 }}>/{cmd.name}</code>
          <div className="k-muted" style={{ fontSize: 12 }}>{cmd.label}</div>
          {cmd.description && (
            <div className="k-muted" style={{ fontSize: 11.5, color: '#94a3b8' }}>{cmd.description}</div>
          )}
        </td>
        <td>{labelOf(KINDS, cmd.kind).replace(/（.*$/, '')}</td>
        <td>
          <code style={{ fontSize: 12, color: '#64748b' }}>{cmd.runsOn}</code>
        </td>
        {needProject && <td>{target}</td>}
        <td>{cmd.enabled ? '有効' : <span style={{ color: '#b45309' }}>無効</span>}</td>
        <td style={{ textAlign: 'right' }}>
          <button type="button" className="btn-ghost" onClick={() => setEditOpen(true)}>
            編集
          </button>{' '}
          <button
            type="button"
            className="btn-danger"
            onClick={() => {
              if (confirm(`コマンド「/${cmd.name}」を削除しますか？`)) del.mutate();
            }}
          >
            削除
          </button>
        </td>
      </tr>
      {editOpen && (
        <tr>
          <td colSpan={colSpan}>
            <CommandForm
              scope={scope}
              initial={cmd}
              projects={projects}
              teams={teams}
              onClose={() => setEditOpen(false)}
              onSubmit={async (v) => {
                await adminApi.updateCommand(cmd.id, v);
                onChanged();
                setEditOpen(false);
              }}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function CommandForm({
  scope,
  initial,
  projects,
  teams,
  onClose,
  onSubmit,
}: {
  scope: CommandScope;
  initial?: CommandMaster;
  projects: Project[];
  teams: Team[];
  onClose: () => void;
  onSubmit: (v: CommandInput) => Promise<void>;
}) {
  const [kind, setKind] = useState(initial?.kind ?? 'custom');
  const [projectId, setProjectId] = useState<number | null>(initial?.projectId ?? null);
  const [teamId, setTeamId] = useState<number | null>(initial?.teamId ?? null);
  const [name, setName] = useState(initial?.name ?? '');
  const [label, setLabel] = useState(initial?.label ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [runsOn, setRunsOn] = useState(initial?.runsOn ?? 'claude-session');
  const [target, setTarget] = useState(initial?.target ?? '');
  const [invokeBy, setInvokeBy] = useState(initial?.invokeBy ?? 'owner');
  const [sort, setSort] = useState(initial?.sort ?? 0);
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const editing = !!initial;

  // scope はページで確定済み。フォームでは選ばせず、必要な対象だけ出す。
  const needProject = scope === 'project' || scope === 'team';
  const needTeam = scope === 'team';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await onSubmit({
        kind,
        scope,
        projectId: needProject ? projectId : null,
        teamId: needTeam ? teamId : null,
        name: name.trim(),
        label: label.trim(),
        description: description.trim() || null,
        runsOn,
        target: target.trim() || null,
        invokeBy,
        sort: Number(sort) || 0,
        enabled,
      });
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  const canSubmit =
    !!name.trim() &&
    !!label.trim() &&
    (!needProject || projectId != null) &&
    (!needTeam || teamId != null);

  return (
    <form onSubmit={submit} style={formBox}>
      <strong>
        {editing ? 'コマンドを編集' : 'コマンドを追加'}
        <span className="k-muted" style={{ fontWeight: 400 }}>（scope: {scope}）</span>
      </strong>
      <div style={fieldRow}>
        <label style={fieldLabel}>
          種別
          <select value={kind} onChange={(e) => setKind(e.target.value)}>
            {KINDS.map((k) => (
              <option key={k.code} value={k.code}>{k.label}</option>
            ))}
          </select>
        </label>
        {needProject && (
          <label style={fieldLabel}>
            プロジェクト
            <select
              value={projectId ?? ''}
              onChange={(e) => setProjectId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">選択…</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.nickname || p.name}</option>
              ))}
            </select>
          </label>
        )}
        {needTeam && (
          <label style={fieldLabel}>
            チーム
            <select
              value={teamId ?? ''}
              onChange={(e) => setTeamId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">選択…</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </label>
        )}
        <label style={fieldLabel}>
          実行先（runsOn）
          <select value={runsOn} onChange={(e) => setRunsOn(e.target.value)}>
            {RUNS_ON.map((r) => (
              <option key={r.code} value={r.code}>{r.label}</option>
            ))}
          </select>
        </label>
      </div>

      <div style={fieldRow}>
        <label style={fieldLabel}>
          名前（/ 無し）
          <input placeholder="例: 本番デプロイ" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label style={{ ...fieldLabel, flex: 1, minWidth: 200 }}>
          表示名（一覧/補完）
          <input placeholder="例: 本番へデプロイ" value={label} onChange={(e) => setLabel(e.target.value)} />
        </label>
      </div>

      <div style={fieldRow}>
        <label style={{ ...fieldLabel, flex: 1, minWidth: 200 }}>
          実体参照（target・任意）
          <input
            placeholder="例: .claude/commands/deploy.md / スキル名"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
          />
        </label>
        <label style={fieldLabel}>
          呼び出し主体
          <select value={invokeBy} onChange={(e) => setInvokeBy(e.target.value)}>
            {INVOKE_BY.map((i) => (
              <option key={i.code} value={i.code}>{i.label}</option>
            ))}
          </select>
        </label>
        <label style={fieldLabel}>
          順
          <input
            type="number"
            value={sort}
            onChange={(e) => setSort(Number(e.target.value))}
            style={{ width: 80 }}
          />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginTop: 18 }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> 有効
        </label>
      </div>

      <label style={{ ...fieldLabel, width: '100%' }}>
        説明（任意・検索対象）
        <textarea
          rows={2}
          placeholder="このコマンドの説明・完了条件など"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>

      {err && <p style={{ color: '#dc2626', margin: 0, fontSize: 13 }}>{err}</p>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" disabled={busy || !canSubmit}>
          保存
        </button>
        <button type="button" className="btn-ghost" onClick={onClose}>
          キャンセル
        </button>
      </div>
    </form>
  );
}

const formBox: React.CSSProperties = {
  marginTop: 12,
  padding: 14,
  border: '1px solid #dbe3ee',
  borderRadius: 10,
  background: '#f8fafc',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};
const fieldRow: React.CSSProperties = { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' };
const fieldLabel: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 12,
  color: '#475569',
};
